import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui';

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (item: any) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  fetchSuggestions: (query: string) => Promise<any[]>;
  debounceDelay?: number;
  /** @deprecated No longer used, kept for backward compatibility */
  fieldType?: 'action' | 'object' | 'vendor' | 'payment';
  onSearchStateChange?: (state: { hasResults: boolean; isSearching: boolean; hasQuery: boolean }) => void;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = '',
  required = false,
  className = '',
  fetchSuggestions,
  debounceDelay = 300,
  onSearchStateChange,
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout for debouncing
    debounceTimeoutRef.current = setTimeout(() => {
      if (newValue.trim()) {
        loadSuggestions(newValue);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
        onSearchStateChange?.({ hasResults: false, isSearching: false, hasQuery: false });
      }
    }, debounceDelay);
  };
  
  const loadSuggestions = async (query: string) => {
    setIsLoading(true);
    setShowDropdown(true);
    onSearchStateChange?.({ hasResults: false, isSearching: true, hasQuery: !!query.trim() });

    try {
      const results = await fetchSuggestions(query);
      setSuggestions(results);
      setActiveIndex(-1);
      onSearchStateChange?.({ hasResults: results.length > 0, isSearching: false, hasQuery: !!query.trim() });
    } catch (error) {
      console.error('Error loading suggestions:', error);
      setSuggestions([]);
      onSearchStateChange?.({ hasResults: false, isSearching: false, hasQuery: !!query.trim() });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSelect = (item: any) => {
    onChange(item.pastTense || item.name);
    if (onSelect) {
      onSelect(item);
    }
    setShowDropdown(false);
    setSuggestions([]);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
        
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSelect(suggestions[activeIndex]);
        }
        break;
        
      case 'Escape':
        setShowDropdown(false);
        setActiveIndex(-1);
        break;
    }
  };
  
  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };
  
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    // Load initial suggestions when component mounts
    if (value) {
      loadSuggestions(value);
    }
  }, []);
  
  return (
    <div className="relative" ref={containerRef}>
      <Input
        type="text"
        attributes={{
          placeholder,
          value,
          onChange: handleInputChange,
          onKeyDown: handleKeyDown,
          onFocus: () => {
            if (value.trim()) {
              loadSuggestions(value);
            }
          },
          required
        }}
        className={`${className} ${showDropdown ? 'rounded-b-none' : ''}`}
      />
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
        </div>
      )}
      
      {/* Suggestions dropdown */}
      {showDropdown && (
        <div className="absolute z-10 w-full bg-light border border-grey_light border-t-0 rounded-b-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.length > 0 ? (
            <ul className="py-1">
              {suggestions.map((item, index) => (
                <li
                  key={item.id}
                  className={`px-3 py-2 cursor-pointer hover:bg-bg_grey_light transition-colors ${
                    index === activeIndex ? 'bg-bg_grey_light' : ''
                  }`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="font-medium text-dark">
                    {item.pastTense || item.name}
                  </div>
                  {item.pastTense && item.pastTense !== item.name && (
                    <div className="text-xs text-grey_dark mt-0.5">{item.name}</div>
                  )}
                  {item.description && (
                    <div className="text-xs text-grey_dark mt-1">{item.description}</div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-4 text-center text-grey_dark">
              {isLoading ? 'Loading...' : 'No suggestions found'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;